import { useEffect, useState, type ReactNode } from 'react';
import { KrabenMark } from './KrabenMark';
import { SLOTS, CREDIT, type Slot, type Pencilled } from '../data/film';
import { listInspirations, type Inspiration } from '../lib/crew';

/** What sits in one slot of the title card. `sheet` crops a card PNG to its sheet; a picture fills the square. */
export interface Piece { img?: string; sheet?: boolean; text?: string; note: string }

/** The pieces the film already has: the crew's kept finds first, then cards pencilled in. */
export function piecesFrom(kept: Inspiration[], pencilled: Record<string, Pencilled> | undefined): Partial<Record<Slot, Piece>> {
  const out: Partial<Record<Slot, Piece>> = {};
  for (const s of SLOTS) {
    const find = kept.find((k) => k.slot === s);
    const pen = pencilled?.[s];
    if (find) out[s] = { text: find.text, note: find.text };
    else if (pen) out[s] = pen.pictureUrl ? { img: pen.pictureUrl, note: `${pen.title} · drawn by your crew` } : pen.cardUrl ? { img: pen.cardUrl, sheet: true, note: pen.title } : { text: pen.headline, note: pen.title };
  }
  return out;
}

/** The crew's kept finds, or none when signed out or offline. `null` until known. */
export function useKeptFinds() {
  const [kept, setKept] = useState<Inspiration[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    listInspirations().then((f) => f.filter((i) => i.status === 'kept')).catch(() => []).then((k) => { if (!cancelled) setKept(k); });
    return () => { cancelled = true; };
  }, []);
  return kept;
}

/**
 * The first film as an old cinema title card (K19 H-06b, owner 2026-09-21):
 * a deep-navy panel with a butter inner border on the app's own cream, one
 * credit per slot, and Kraben peeking over the top. It replaced a full-screen
 * dark filmstrip that looked like it came from another app.
 *
 * `next` is the slot about to be filled (dashed butter, gently pulsing);
 * `landing` is the one just filled (it drops in and glows). `slotRef` hands
 * back each slot's box, for a picture that flies into it. There is no title
 * until the film has one: "Untitled" read as a gap (owner, 2026-09-21).
 */
export function FilmCard({ pieces, next, landing, slotRef, title, kicker, footer }: {
  pieces: Partial<Record<Slot, Piece>>; next?: Slot | null; landing?: Slot | null; slotRef?: (s: Slot, el: HTMLDivElement | null) => void;
  title?: string; kicker?: string; footer?: ReactNode;
}) {
  return (
    <div className="film-card" aria-label="Your first film">
      <KrabenMark size={64} style={{ position: 'absolute', right: 12, top: -44 }} />
      <div className="film-inner">
        <div className="film-head">
          <span className="film-kicker">{kicker ?? `Your first film · ${SLOTS.filter((s) => pieces[s]).length} of ${SLOTS.length}`}</span>
          {title && <b className="film-title">{title}</b>}
        </div>
        {SLOTS.map((s) => {
          const p = pieces[s];
          const cls = ['credit', p ? 'filled' : '', s === next && !p ? 'next' : '', s === landing ? 'landing' : ''].join(' ');
          return (
            <div key={s} className={cls}>
              <span className="credit-label">{CREDIT[s].label}</span>
              <div className="credit-slot" ref={(el) => slotRef?.(s, el)}>
                {p?.img && (p.sheet ? <div className="mini-sheet"><img src={p.img} alt={p.note} /></div> : <img src={p.img} alt={p.note} />)}
                {p && !p.img && <em>{p.text}</em>}
              </div>
              <span className="credit-note">{p ? p.note : s === next ? 'yours, next' : CREDIT[s].empty}</span>
            </div>
          );
        })}
        <div className="film-perf" aria-hidden />
        {footer}
      </div>
    </div>
  );
}
